// src/services/paystackService.js
import { PAYSTACK_CONFIG, PAYMENT_TYPES } from '../../config/paystack';

// Helper to get the current origin URL
const getCallbackUrl = () => {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/payment-confirmation`;
  }
  return PAYSTACK_CONFIG.callbackUrl || 'https://pcfgym.co.ke/payment-confirmation';
};

// Generate unique reference
const generateReference = (prefix = 'PCF') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10).toUpperCase();
  return `${prefix}_${timestamp}_${random}`;
};

export const initializePayment = async (paymentDetails, user) => {
  // Validation
  if (!paymentDetails || !user) {
    console.error('Missing payment details or user information');
    return { 
      success: false, 
      error: 'Missing payment details or user information' 
    };
  }

  if (!paymentDetails.amount || paymentDetails.amount <= 0) {
    console.error('Invalid payment amount:', paymentDetails.amount);
    return { 
      success: false, 
      error: 'Invalid payment amount' 
    };
  }

  if (!user.email) {
    console.error('User email is required');
    return { 
      success: false, 
      error: 'User email is required for payment' 
    };
  }

  // Check if Paystack keys are configured
  if (!PAYSTACK_CONFIG.secretKey || PAYSTACK_CONFIG.secretKey === 'undefined') {
    console.error('Paystack secret key is not configured');
    return { 
      success: false, 
      error: 'Payment system is not properly configured. Please contact support.' 
    };
  }

  try {
    const amountInCents = Math.round(paymentDetails.amount * 100);
    let prefix = 'PCF';
    
    if (paymentDetails.type === 'membership') prefix = 'MBR';
    else if (paymentDetails.type === 'trainer_group') prefix = 'GRP';
    else if (paymentDetails.type === 'trainer_personal') prefix = 'PTR';
    else if (paymentDetails.type === 'shop') prefix = 'ORD';
    
    const reference = generateReference(prefix);
    const callbackUrl = getCallbackUrl();
    
    const requestBody = {
      email: user.email,
      amount: amountInCents,
      currency: 'KES',
      reference: reference,
      callback_url: callbackUrl,
      metadata: {
        userId: user.uid,
        paymentType: paymentDetails.type,
        planName: paymentDetails.planName,
        planPeriod: paymentDetails.period || 'single',
        website: process.env.REACT_APP_WEBSITE_URL || 'pcfgym.co.ke',
        timestamp: new Date().toISOString(),
        customFields: paymentDetails.customFields || {},
      },
    };

    // Add customer info if available
    if (user.displayName) {
      requestBody.metadata.customerName = user.displayName;
    }
    
    if (paymentDetails.phone) {
      requestBody.metadata.customerPhone = paymentDetails.phone;
    }

    console.log('Initializing Paystack payment:', {
      email: user.email,
      amount: amountInCents,
      type: paymentDetails.type,
      reference: reference,
      environment: process.env.REACT_APP_ENVIRONMENT || 'development'
    });

    const response = await fetch('https://api.paystack.co/transaction/initialize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${PAYSTACK_CONFIG.secretKey}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Paystack API error response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    
    if (data.status) {
      console.log('Payment initialized successfully:', {
        reference: data.data.reference,
        authorizationUrl: data.data.authorization_url
      });
      
      // Store payment reference in session for recovery
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('current_payment_reference', data.data.reference);
        sessionStorage.setItem('current_payment_type', paymentDetails.type);
      }
      
      return {
        success: true,
        authorizationUrl: data.data.authorization_url,
        reference: data.data.reference,
      };
    }
    
    console.error('Paystack initialization failed:', data.message);
    return { 
      success: false, 
      error: data.message || 'Payment initialization failed' 
    };
    
  } catch (error) {
    console.error('Payment initialization error:', error);
    return { 
      success: false, 
      error: error.message || 'Network error. Please check your connection and try again.' 
    };
  }
};

export const verifyPayment = async (reference) => {
  if (!reference) {
    console.error('No payment reference provided for verification');
    return { 
      success: false, 
      error: 'No payment reference provided' 
    };
  }

  // Check if Paystack keys are configured
  if (!PAYSTACK_CONFIG.secretKey || PAYSTACK_CONFIG.secretKey === 'undefined') {
    console.error('Paystack secret key is not configured');
    return { 
      success: false, 
      error: 'Payment system is not properly configured.' 
    };
  }

  try {
    console.log('Verifying payment:', reference);
    
    const response = await fetch(`https://api.paystack.co/transaction/verify/${reference}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${PAYSTACK_CONFIG.secretKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Paystack verification error response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    
    if (data.status) {
      const transaction = data.data;
      
      if (transaction.status === 'success') {
        console.log('Payment verified successfully:', {
          reference: transaction.reference,
          amount: transaction.amount / 100,
          paidAt: transaction.paidAt
        });
        
        // Clear session storage
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem('current_payment_reference');
          sessionStorage.removeItem('current_payment_type');
        }
        
        return {
          success: true,
          data: {
            reference: transaction.reference,
            amount: transaction.amount / 100,
            currency: transaction.currency,
            paidAt: transaction.paidAt,
            metadata: transaction.metadata,
            customer: transaction.customer,
            gateway_response: transaction.gateway_response,
            channel: transaction.channel,
          },
        };
      } else {
        console.warn('Payment not successful:', transaction.status, transaction.gateway_response);
        return { 
          success: false, 
          error: `Payment ${transaction.status}: ${transaction.gateway_response || 'Transaction not successful'}` 
        };
      }
    }
    
    console.error('Paystack verification failed:', data.message);
    return { 
      success: false, 
      error: data.message || 'Payment verification failed' 
    };
    
  } catch (error) {
    console.error('Payment verification error:', error);
    return { 
      success: false, 
      error: error.message || 'Network error. Please contact support.' 
    };
  }
};

// Helper function to check payment status
export const checkPaymentStatus = async (reference) => {
  try {
    const response = await fetch(`https://api.paystack.co/transaction/verify/${reference}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${PAYSTACK_CONFIG.secretKey}`,
      },
    });
    
    const data = await response.json();
    
    if (data.status) {
      return {
        status: data.data.status,
        amount: data.data.amount / 100,
        reference: data.data.reference,
        paidAt: data.data.paidAt,
      };
    }
    
    return { status: 'unknown', error: data.message };
  } catch (error) {
    console.error('Check payment status error:', error);
    return { status: 'error', error: error.message };
  }
};

// Helper to format amount for display
export const formatAmount = (amount, currency = 'KES') => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper to get payment type label
export const getPaymentTypeLabel = (type) => {
  const labels = {
    membership: 'Membership Subscription',
    trainer_group: 'Group Training Session',
    trainer_personal: 'Personal Training Session',
    shop: 'Shop Purchase',
  };
  return labels[type] || 'Payment';
};

// Retry payment initialization with exponential backoff
export const retryInitializePayment = async (paymentDetails, user, maxRetries = 3) => {
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    console.log(`Payment initialization attempt ${attempt}/${maxRetries}`);
    
    const result = await initializePayment(paymentDetails, user);
    
    if (result.success) {
      return result;
    }
    
    lastError = result.error;
    
    if (attempt < maxRetries) {
      const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
      console.log(`Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  return {
    success: false,
    error: `Failed after ${maxRetries} attempts: ${lastError}`,
  };
};

// Export all functions
export default {
  initializePayment,
  verifyPayment,
  checkPaymentStatus,
  formatAmount,
  getPaymentTypeLabel,
  retryInitializePayment,
};